<script setup lang="ts">
import useCityStore from '~~/app/store/city'
import FaqItem from '~~/app/components/Networks/FaqItem.vue'
import CloseCityItem from '~~/app/components/Cities/CloseCityItem.vue'
import { getChargemapUrl } from '~/utils/url'

const cityStore = useCityStore()

defineEmits(['scrollToMap'])
</script>

<template>
    <div class="faq">
        <FaqItem with-border rounded-top>
            <template #question>
                <i18n-t keypath="cities.faq.item1.question" scope="global">
                    <template #city>
                        {{ cityStore.name }}
                    </template>
                </i18n-t>
            </template>
            <template #answer>
                <span class="answer flex flex-col gap-2">
                    <span>
                        <i18n-t
                            keypath="cities.faq.item1.answer1"
                            scope="global"
                        >
                            <template #city>
                                {{ cityStore.name }}
                            </template>
                            <template #stationsCount>
                                {{
                                    $t(
                                        'cities.charging_stations',
                                        cityStore.stationsTotalCount
                                    )
                                }}
                            </template>
                            <template #stationsInCity>
                                <span class="font-bold">
                                    {{
                                        $t(
                                            'cities.faq.item1.stations_in_city',
                                            {
                                                city: cityStore.name
                                            }
                                        )
                                    }}
                                </span>
                            </template>
                            <template #chargemapCard>
                                <NuxtLink
                                    class="link"
                                    :to="getChargemapUrl('/map')"
                                    :title="$t('networks.faq.chargemap_map')"
                                >
                                    {{ $t('networks.faq.chargemap_map') }}
                                </NuxtLink>
                            </template>
                        </i18n-t>
                    </span>
                    <span>
                        <i18n-t
                            keypath="cities.faq.item1.answer2"
                            scope="global"
                        >
                            <template #city>
                                {{ cityStore.name }}
                            </template>
                            <template #closeCity1>
                                <CloseCityItem
                                    :close-city="
                                        cityStore.cityData?.citiesAround[1]
                                    "
                                />
                            </template>
                            <template #closeCity2>
                                <CloseCityItem
                                    :close-city="
                                        cityStore.cityData?.citiesAround[2]
                                    "
                                />
                            </template>
                            <template #closeCity3>
                                <CloseCityItem
                                    :close-city="
                                        cityStore.cityData?.citiesAround[3]
                                    "
                                />
                            </template>
                        </i18n-t>
                    </span>
                </span>
            </template>
        </FaqItem>
        <FaqItem with-border>
            <template #question>
                <i18n-t keypath="cities.faq.item2.question" scope="global">
                    <template #city>
                        {{ cityStore.name }}
                    </template>
                </i18n-t>
            </template>
            <template #answer>
                <span class="answer">
                    <i18n-t keypath="cities.faq.item2.answer1" scope="global">
                        <template #pools>
                            <span class="font-bold">
                                {{
                                    $t('cities.faq.item1.stations_in_city', {
                                        city: cityStore.name
                                    })
                                }}
                            </span>
                        </template>
                    </i18n-t>
                    <ul class="list-disc list-inside py-2">
                        <li>
                            {{ $t('cities.faq.item2.answer2') }}
                        </li>
                        <li>
                            {{ $t('cities.faq.item2.answer3') }}
                        </li>
                    </ul>
                    <i18n-t keypath="cities.faq.item2.answer4" scope="global">
                        <template #chargemapMembers>
                            <span class="font-bold">
                                {{ $t('cities.faq.chargemap_members') }}
                            </span>
                        </template>
                    </i18n-t>
                </span>
            </template>
        </FaqItem>
        <FaqItem with-border>
            <template #question>
                <i18n-t keypath="cities.faq.item3.question" scope="global">
                    <template #city>
                        {{ cityStore.name }}
                    </template>
                </i18n-t>
            </template>
            <template #answer>
                <span class="answer">
                    <i18n-t keypath="cities.faq.item3.answer" scope="global">
                        <template #city>
                            {{ cityStore.name }}
                        </template>
                        <template #freeStationsCount>
                            <span class="font-bold">
                                {{
                                    $t('cities.faq.free_stations_count', {
                                        count: $n(cityStore.stationsFreeCount)
                                    })
                                }}
                            </span>
                        </template>
                        <template #chargemapCard>
                            <NuxtLink
                                class="link"
                                :to="getChargemapUrl('/map')"
                                :title="$t('word.the_chargemap_map')"
                            >
                                {{ $t('word.the_chargemap_map') }}
                            </NuxtLink>
                        </template>
                    </i18n-t>
                </span>
            </template>
        </FaqItem>
        <FaqItem with-border>
            <template #question>
                <i18n-t keypath="cities.faq.item4.question" scope="global">
                    <template #city>
                        {{ cityStore.name }}
                    </template>
                </i18n-t>
            </template>
            <template #answer>
                <span class="answer">
                    <i18n-t keypath="cities.faq.item4.answer" scope="global">
                        <template #fastStationsCount>
                            <span class="font-bold">
                                {{
                                    $t('cities.faq.fast_stations_count', {
                                        count: $n(
                                            cityStore.stationsFastChargeCount
                                        )
                                    })
                                }}
                            </span>
                        </template>
                        <template #city>
                            {{ cityStore.name }}
                        </template>
                        <template #chargemapCard>
                            <NuxtLink
                                class="link"
                                :to="getChargemapUrl('/map')"
                                :title="$t('word.the_stations_map')"
                            >
                                {{ $t('word.the_stations_map') }}
                            </NuxtLink>
                        </template>
                    </i18n-t>
                </span>
            </template>
        </FaqItem>
        <FaqItem rounded-bottom>
            <template #question>
                <i18n-t keypath="cities.faq.item5.question" scope="global">
                    <template #city>
                        {{ cityStore.name }}
                    </template>
                </i18n-t>
            </template>
            <template #answer>
                <span class="answer">
                    <i18n-t keypath="cities.faq.item5.answer1" scope="global">
                        <template #part1>
                            <span class="font-bold">
                                {{ $t('cities.faq.item5.answer1_1') }}
                            </span>
                        </template>
                    </i18n-t>
                    <br />
                    <br />
                    <i18n-t keypath="cities.faq.item5.answer2" scope="global">
                        <template #city>
                            {{ cityStore.name }}
                        </template>
                        <template #network1>
                            {{ cityStore.topNetworks[0]?.name || '' }}
                        </template>
                        <template #network2>
                            {{ cityStore.topNetworks[1]?.name || '' }}
                        </template>
                        <template #network3>
                            {{ cityStore.topNetworks[2]?.name || '' }}
                        </template>
                    </i18n-t>
                    <br />
                    <br />
                    <i18n-t keypath="cities.faq.item5.answer3" scope="global">
                        <template #part1>
                            <span class="font-bold">
                                {{ $t('cities.faq.item5.answer3_1') }}
                            </span>
                        </template>
                        <template #stationsMap>
                            <button
                                class="link"
                                type="button"
                                @click="$emit('scrollToMap')"
                            >
                                {{
                                    $t('cities.faq.city_stations_map', {
                                        city: cityStore.name
                                    })
                                }}
                            </button>
                        </template>
                    </i18n-t>
                    <br />
                    <br />
                    <span class="text-[15px]">
                        <i18n-t
                            keypath="cities.faq.item5.answer4"
                            scope="global"
                        >
                            <template #pass>
                                <NuxtLink
                                    class="link"
                                    :to="getChargemapUrl('/pass')"
                                    :title="$t('word.the_chargemap_pass')"
                                >
                                    {{ $t('word.the_chargemap_pass') }}
                                </NuxtLink>
                            </template>
                        </i18n-t>
                    </span>
                </span>
            </template>
        </FaqItem>
    </div>
</template>

<style lang="scss" scoped>
.faq {
    @apply w-full p-6 md:py-12 md:px-0 bg-sky-100 flex flex-col justify-center items-center md:gap-7 mt-12;

    .answer {
        @apply text-zinc-800 text-base font-inter leading-6 md:leading-relaxed;
    }

    .link {
        @apply underline text-sky-500;
    }
}
</style>
